import { AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react'
import { Card, CardContent } from '@/presentation/components/ui/card'
import { Button } from '@/presentation/components/ui/button'

export interface ErrorDisplayProps {
  title?: string
  errorType?: 'timeout' | 'network' | 'server' | 'unknown'
  onRetry?: () => void
  isRetrying?: boolean
  showDetails?: boolean
  errorDetails?: string
}

export function ErrorDisplay({
  title,
  errorType = 'unknown',
  onRetry,
  isRetrying = false,
}: ErrorDisplayProps) {
  const getIcon = () => {
    switch (errorType) {
      case 'timeout':
        return <AlertCircle className="w-10 h-10 text-pinkGarupa" />
      case 'network':
        return <WifiOff className="w-10 h-10 text-pinkGarupa" />
      case 'server':
        return <AlertCircle className="w-10 h-10 text-pinkGarupa" />
      default:
        return <AlertCircle className="w-10 h-10 text-pinkGarupa" />
    }
  }

  const getDefaultTitle = () => {
    switch (errorType) {
      case 'timeout':
        return 'Tempo de Espera Esgotado'
      case 'network':
        return 'Sem Conexão'
      case 'server':
        return 'Erro no Servidor'
      default:
        return 'Ocorreu um Erro'
    }
  }

  const displayTitle = title || getDefaultTitle()

  return (
    <div className="flex min-h-[400px] w-full items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 space-y-6 text-center">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 bg-pinkGarupa/30 dark:bg-pinkGarupa/30 rounded-full flex items-center justify-center">
                {getIcon()}
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {displayTitle}
            </h2>
          </div>

          {/* Retry Button */}
          {onRetry && (
            <div className="space-y-3">
              <Button
                onClick={onRetry}
                disabled={isRetrying}
                className="w-full bg-pinkGarupa hover:bg-pinkGarupa/60 text-white"
                size="lg"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Tentando novamente...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Tentar Novamente
                  </>
                )}
              </Button>

              {errorType === 'network' && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Wifi className="h-4 w-4" />
                  <span>Verifique sua conexão com a internet</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function InlineErrorDisplay({
  message,
  onRetry,
  isRetrying = false,
}: {
  message: string
  onRetry?: () => void
  isRetrying?: boolean
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-pinkGarupa/30 bg-pinkGarupa/10 p-4 dark:border-pinkGarupa/40 dark:bg-pinkGarupa/20">
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-pinkGarupa dark:text-pinkGarupa" />
        <p className="text-sm text-gray-800 dark:text-gray-300">{message}</p>
      </div>
      {onRetry && (
        <Button
          onClick={onRetry}
          disabled={isRetrying}
          className="bg-pinkGarupa hover:bg-pinkGarupa/80 text-white"
          size="sm"
        >
          {isRetrying ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            'Tentar Novamente'
          )}
        </Button>
      )}
    </div>
  )
}
